import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '../AdminLayout';
import { getBlogAdmin, createBlog, updateBlog } from '../../services/adminService';
import { uploadToCloudinary } from '../../utils/uploadToCloudinary';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { serverTimestamp } from 'firebase/firestore';

const CATEGORIES = ['Bridal', 'Makeup Tips', 'Skincare', 'Tutorials', 'Courses', 'Behind the Scenes'];

const slugify = (text) =>
  text.toLowerCase().replace(/[^a-z0-9\s-]/g, '').trim().replace(/\s+/g, '-');

const QUILL_MODULES = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['blockquote', 'link'],
    ['clean'],
  ],
};

const BLANK = {
  title: '', slug: '', category: '', excerpt: '',
  content: '', coverImage: '', seoTitle: '', seoDescription: '', published: false,
};

export default function AdminBlogEditor() {
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const [form, setForm] = useState(BLANK);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(isNew);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isNew) {
      getBlogAdmin(id).then(data => {
        if (data) setForm(data);
        setLoaded(true);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const set = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setForm(f => ({ ...f, title, slug: isNew ? slugify(title) : f.slug }));
  };

  const handleCoverImg = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try { const url = await uploadToCloudinary(file); set('coverImage', url); }
    catch { alert('Upload failed'); }
    finally { setUploading(false); }
  };

  const handleSave = async (publish) => {
    if (!form.title || !form.content) { alert('Title and content are required.'); return; }
    setSaving(true);
    try {
      const data = {
        ...form,
        published: publish ?? form.published,
        publishedAt: (publish || form.published) ? serverTimestamp() : null,
        seoTitle: form.seoTitle || form.title,
        seoDescription: form.seoDescription || form.excerpt,
      };
      if (isNew) {
        await createBlog(data);
      } else {
        await updateBlog(id, data);
      }
      navigate('/admin/blogs');
    } catch (err) {
      alert('Save failed: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return <AdminLayout title="Blog Editor"><div className="admin-spinner">Loading…</div></AdminLayout>;

  return (
    <AdminLayout title={isNew ? 'New Blog Post' : 'Edit Blog Post'}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 24, alignItems: 'start' }}>

        {/* Main editor column */}
        <div>
          <div className="admin-section">
            <div className="admin-section-body">
              <div className="admin-form">
                <div>
                  <label style={{ fontWeight: 700, fontSize: 13, color: 'var(--a-text-muted)' }}>Title</label>
                  <input value={form.title} onChange={handleTitleChange} placeholder="Blog post title…" style={{ fontSize: 22, fontWeight: 700, border: 'none', borderBottom: '2px solid var(--a-border)', borderRadius: 0, padding: '8px 0' }} />
                </div>
                <div>
                  <label style={{ fontWeight: 700, fontSize: 13, color: 'var(--a-text-muted)' }}>Slug (URL)</label>
                  <input value={form.slug} onChange={e => set('slug', e.target.value)} placeholder="url-friendly-slug" />
                </div>
              </div>
            </div>
          </div>

          <div className="admin-section">
            <div className="admin-section-header"><h2>Content</h2></div>
            <div className="admin-section-body">
              <ReactQuill
                theme="snow"
                value={form.content}
                onChange={(val) => set('content', val)}
                modules={QUILL_MODULES}
                style={{ minHeight: 360, fontSize: 15 }}
              />
            </div>
          </div>

          <div className="admin-section">
            <div className="admin-section-header"><h2>SEO</h2></div>
            <div className="admin-section-body">
              <div className="admin-form">
                <div><label>SEO Title (leave blank to use post title)</label><input value={form.seoTitle} onChange={e => set('seoTitle', e.target.value)} placeholder={form.title} /></div>
                <div><label>Meta Description (leave blank to use excerpt)</label><textarea rows={2} value={form.seoDescription} onChange={e => set('seoDescription', e.target.value)} placeholder={form.excerpt} /></div>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar settings column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          <div className="admin-section">
            <div className="admin-section-header"><h2>Publish</h2></div>
            <div className="admin-section-body admin-form">
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="a-btn a-btn-ghost" style={{ flex: 1 }} onClick={() => handleSave(false)} disabled={saving}>
                  {saving ? '…' : 'Save Draft'}
                </button>
                <button className="a-btn a-btn-primary" style={{ flex: 1 }} onClick={() => handleSave(true)} disabled={saving}>
                  {saving ? '…' : form.published ? 'Update' : 'Publish'}
                </button>
              </div>
              <button className="a-btn a-btn-ghost" style={{ width: '100%', marginTop: 4 }} onClick={() => navigate('/admin/blogs')}>
                ← Back to Blogs
              </button>
            </div>
          </div>

          <div className="admin-section">
            <div className="admin-section-header"><h2>Cover Image</h2></div>
            <div className="admin-section-body">
              <label className="upload-box">
                <input type="file" accept="image/*" onChange={handleCoverImg} />
                {uploading ? <p>Uploading…</p> : form.coverImage
                  ? <img src={form.coverImage} alt="" style={{ width: '100%', borderRadius: 6 }} />
                  : <p>Click to upload cover image</p>}
              </label>
            </div>
          </div>

          <div className="admin-section">
            <div className="admin-section-header"><h2>Details</h2></div>
            <div className="admin-section-body">
              <div className="admin-form">
                <div>
                  <label>Category</label>
                  <select value={form.category} onChange={e => set('category', e.target.value)}>
                    <option value="">Select category</option>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label>Excerpt (shown on listing page)</label>
                  <textarea rows={3} value={form.excerpt} onChange={e => set('excerpt', e.target.value)} placeholder="A short summary of the post…" />
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </AdminLayout>
  );
}
